const FORMULARIO_CLIPBOARD_MARKER = 'actio:formulario-global';
const FORMULARIO_CLIPBOARD_VERSION = 1;
const CLIENT_ID_FIELD = '_formularioGlobalClientId';

// Prefixos que identificam o codec usado no conteúdo da área de transferência
const CODEC_GZIP = 'g:';
const CODEC_RAW = 'r:';

const hasValue = (value) => String(value || '').trim().length > 0;

const stripIdentifiers = (item) => {
  const next = { ...item };
  delete next[CLIENT_ID_FIELD];
  delete next.id;
  return next;
};

const stripOpcoesIdentifiers = (opcoes = []) =>
  opcoes.map((opcao) => stripIdentifiers(opcao));

const stripPerguntasIdentifiers = (perguntas = []) =>
  perguntas.map((pergunta) => {
    const next = stripIdentifiers(pergunta);

    return {
      ...next,
      opcoes: stripOpcoesIdentifiers(next.opcoes || []),
      ...(next.linhas && { linhas: stripOpcoesIdentifiers(next.linhas) }),
      ...(next.colunas && { colunas: stripOpcoesIdentifiers(next.colunas) }),
    };
  });

const buildClipboardFormulario = (formulario) => {
  const normalized = formulario || {};

  return {
    titulo: normalized.titulo || '',
    descricao: normalized.descricao || '',
    secoes: (normalized.secoes || []).map((secao) => {
      const next = stripIdentifiers(secao);

      return {
        ...next,
        perguntas: stripPerguntasIdentifiers(next.perguntas || []),
      };
    }),
    perguntas: stripPerguntasIdentifiers(normalized.perguntas || []),
  };
};

const bytesToBase64 = (bytes) => {
  let binary = '';
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }

  return btoa(binary);
};

const base64ToBytes = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
};

const temCompressionStream = () =>
  typeof CompressionStream !== 'undefined' &&
  typeof DecompressionStream !== 'undefined';

const gzipString = async (text) => {
  const input = new TextEncoder().encode(text);
  const stream = new Blob([input])
    .stream()
    .pipeThrough(new CompressionStream('gzip'));
  const buffer = await new Response(stream).arrayBuffer();

  return new Uint8Array(buffer);
};

const gunzipBytes = async (bytes) => {
  const stream = new Blob([bytes])
    .stream()
    .pipeThrough(new DecompressionStream('gzip'));
  const buffer = await new Response(stream).arrayBuffer();

  return new TextDecoder().decode(buffer);
};

export const isFormularioVazio = (formulario) => {
  const normalized = formulario || {};

  return (
    !hasValue(normalized.titulo) &&
    !hasValue(normalized.descricao) &&
    (normalized.secoes || []).length === 0 &&
    (normalized.perguntas || []).length === 0
  );
};

const serializeFormularioParaClipboard = async (formulario) => {
  const payload = JSON.stringify({
    tipo: FORMULARIO_CLIPBOARD_MARKER,
    versao: FORMULARIO_CLIPBOARD_VERSION,
    formulario: buildClipboardFormulario(formulario),
  });

  if (temCompressionStream()) {
    const compressed = await gzipString(payload);
    return `${CODEC_GZIP}${bytesToBase64(compressed)}`;
  }

  return `${CODEC_RAW}${bytesToBase64(new TextEncoder().encode(payload))}`;
};

const parseFormularioDoClipboard = async (text) => {
  if (!text) return null;

  let parsed;
  try {
    let payload;

    if (text.startsWith(CODEC_GZIP)) {
      payload = await gunzipBytes(base64ToBytes(text.slice(CODEC_GZIP.length)));
    } else if (text.startsWith(CODEC_RAW)) {
      payload = new TextDecoder().decode(
        base64ToBytes(text.slice(CODEC_RAW.length))
      );
    } else {
      return null;
    }

    parsed = JSON.parse(payload);
  } catch {
    return null;
  }

  if (
    !parsed ||
    parsed.tipo !== FORMULARIO_CLIPBOARD_MARKER ||
    !parsed.formulario
  ) {
    return null;
  }

  return parsed.formulario;
};

const copyTextToClipboard = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }

  const input = document.createElement('textarea');
  input.value = value;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(input);

  return copied;
};

export const copiarFormularioParaAreaTransferencia = async (formulario) => {
  const conteudo = await serializeFormularioParaClipboard(formulario);
  return copyTextToClipboard(conteudo);
};

export const lerFormularioDaAreaTransferencia = async () => {
  const text = await navigator.clipboard?.readText?.();
  return parseFormularioDoClipboard(text);
};
